import React, { useMemo } from "react";
import BoardView, {
  BoardColumn,
  useBoardColumnColors,
} from "@/components/board-view";
import ResourceCard, { SourceIcon } from "@/components/resource-card";
import { TrackerViewProps } from "../types";
import { Tracker } from "@/models/tracker";
import { ResourceGroup, getGroupRefId } from "@/models/resource-group";
import { statusColors } from "@/styles/colors";
import ResourceGroupCard from "@/components/resource-group-card";
import { trackerGalleryConfig } from "@/config/resource-gallery-configs";

import type { ViewSize } from "@/components/layout/gallery-layout/types";

const BOARD_COLUMN_WIDTH: Record<ViewSize, string> = {
  small: "260px",
  medium: "320px",
  large: "420px",
};

const BOARD_GAP: Record<ViewSize, string> = {
  small: "gap-2",
  medium: "gap-4",
  large: "gap-5",
};

const BOARD_HEADER_PAD: Record<ViewSize, string> = {
  small: "px-3 py-2",
  medium: "px-4 py-3",
  large: "px-5 py-4",
};

const BOARD_HEADER_TITLE: Record<ViewSize, string> = {
  small: "text-xs",
  medium: "text-sm",
  large: "text-base",
};

const BOARD_CONTENT_PAD: Record<ViewSize, string> = {
  small: "p-2 space-y-1.5",
  medium: "p-3 space-y-3",
  large: "p-4 space-y-4",
};

const TrackerBoardView: React.FC<TrackerViewProps> = ({
  trackers,
  onToggleActive,
  getTrackerValue,
  trackersTagsMap,
  onTagsUpdate,
  groupBy = "none",
  groupedTrackers,
  resourceGroups,
  expandedFolders,
  onToggleFolder,
  viewSize = "medium",
}) => {
  const { getStatusColor, getColorByIndex, chartColors } =
    useBoardColumnColors();

  // Fonction pour obtenir la couleur d'une colonne selon son type
  const getColumnColor = (columnId: string, groupByType: string): string => {
    if (groupByType === "status") {
      return getStatusColor(columnId);
    }
    return getColorByIndex(columnId);
  };

  // Filter trackers not in folders
  const trackersNotInFolders = useMemo(() => {
    return trackers.filter((t) => !t.group);
  }, [trackers]);

  // Créer les colonnes selon le critère de groupement
  const boardColumns: BoardColumn<Tracker>[] = useMemo(() => {
    if (groupBy === "none") {
      // Par défaut, grouper par statut (only ungrouped trackers)
      const active = trackersNotInFolders.filter(
        (t) => !t.is_import && t.status === "active",
      );
      const inactive = trackersNotInFolders.filter(
        (t) => !t.is_import && t.status !== "active",
      );
      const imports = trackersNotInFolders.filter((t) => t.is_import);
      const columns: BoardColumn<Tracker>[] = [
        {
          id: "active",
          title: "Actifs",
          items: active,
          color: statusColors.active,
        },
        {
          id: "inactive",
          title: "Inactifs",
          items: inactive,
          color: statusColors.inactive,
        },
      ];
      if (imports.length > 0) {
        columns.push({
          id: "import",
          title: "Import",
          items: imports,
          color: chartColors.vibrant[0],
        });
      }
      return columns;
    }

    if (!groupedTrackers) return [];

    // Utiliser les groupes selon le critère sélectionné (only ungrouped trackers)
    return Object.entries(groupedTrackers).map(([groupKey, groupTrackers]) => ({
      id: groupKey,
      title: groupKey,
      items: groupTrackers.filter((t) => !t.group),
      color: getColumnColor(groupKey, groupBy),
      icon:
        groupBy === "source" ? (
          <SourceIcon source={groupKey} size="sm" />
        ) : undefined,
    }));
  }, [trackersNotInFolders, groupBy, groupedTrackers, chartColors.vibrant]);

  const renderTracker = (tracker: Tracker) => (
    <ResourceCard
      tracker={tracker}
      resourceType="tracker"
      onToggleActive={onToggleActive}
      onTagsUpdate={onTagsUpdate}
      tagsMap={trackersTagsMap}
      viewSize={viewSize}
    />
  );

  // Si on n'a pas de folders ou pas les fonctions nécessaires, utiliser la vue standard
  if (
    !resourceGroups ||
    resourceGroups.length === 0 ||
    !onToggleFolder ||
    !expandedFolders
  ) {
    return (
      <BoardView
        columns={boardColumns}
        renderItem={renderTracker}
        emptyMessage="Aucun tracker dans ce groupe"
        viewSize={viewSize}
      />
    );
  }

  const colWidth = BOARD_COLUMN_WIDTH[viewSize];

  // Si on a des folders, créer une vue custom
  return (
    <div className={`flex ${BOARD_GAP[viewSize]} overflow-x-auto pb-4 h-full`}>
      {boardColumns.map((column) => {
        const { getGroupKey } = trackerGalleryConfig;

        // Compute the column ID for a tracker, matching the column creation logic
        const getTrackerColumnId = (tracker: Tracker): string => {
          if (groupBy !== "none") return getGroupKey(tracker, groupBy) as string;
          if (tracker.is_import) return "import";
          return tracker.status === "active" ? "active" : "inactive";
        };

        // Helper to get all folders hierarchically
        const getFoldersHierarchically = () => {
          const result: Array<{ folder: ResourceGroup; level: number }> = [];

          const addFoldersRecursively = (
            parentId: string | null,
            level: number = 0,
          ) => {
            const foldersAtLevel = resourceGroups.filter((folder) => {
              const folderParentId = folder.parent?.id || null;
              return folderParentId === parentId;
            });

            foldersAtLevel.forEach((folder) => {
              // Check if this folder has trackers in current column
              const trackersInFolder = trackers.filter((t) => {
                const groupId = getGroupRefId(t.group);
                return groupId === folder.id;
              });

              const hasTrackersInColumn = trackersInFolder.some((tracker) => {
                return getTrackerColumnId(tracker) === column.id;
              });

              // Check if any child folder might have trackers in this column
              const checkChildFolders = (parentId: string): boolean => {
                const childFolders = resourceGroups.filter(
                  (f) => f.parent?.id === parentId,
                );

                for (const childFolder of childFolders) {
                  const childTrackers = trackers.filter((t) => {
                    const groupId = getGroupRefId(t.group);
                    return groupId === childFolder.id;
                  });

                  const childHasTrackersInColumn = childTrackers.some(
                    (tracker) => {
                      return getTrackerColumnId(tracker) === column.id;
                    },
                  );

                  if (childHasTrackersInColumn) return true;
                  if (checkChildFolders(childFolder.id)) return true;
                }

                return false;
              };

              const hasChildrenInColumn = checkChildFolders(folder.id);

              // Show folder if it has trackers in this column OR if it has children with trackers
              if (hasTrackersInColumn || hasChildrenInColumn) {
                result.push({ folder, level });
                if (expandedFolders.has(folder.id)) {
                  addFoldersRecursively(folder.id, level + 1);
                }
              }
            });
          };

          addFoldersRecursively(null, 0);
          return result;
        };

        const foldersWithLevels = getFoldersHierarchically();

        // Get ungrouped trackers in this column
        const ungroupedTrackersInColumn = column.items.filter((t) => !t.group);

        // Count total items including folders
        const totalItemsInColumn =
          foldersWithLevels.length + ungroupedTrackersInColumn.length;

        return (
          <div
            key={column.id}
            className="flex-shrink-0 flex flex-col h-full"
            style={{ width: colWidth, minWidth: colWidth }}
          >
            <div
              className="bg-surface-elevated rounded-arc-lg border border-arc-border shadow-arc-sm overflow-hidden flex flex-col"
              style={{
                borderColor: column.color + "20",
                backgroundColor: column.color + "08",
              }}
            >
              <div
                className={`${BOARD_HEADER_PAD[viewSize]} border-b border-arc-border flex items-center gap-2`}
                style={{ backgroundColor: column.color + "15" }}
              >
                {column.icon}
                <span className={`${BOARD_HEADER_TITLE[viewSize]} font-medium text-content-primary`}>
                  {column.title}
                </span>
                <span
                  className="ml-auto text-xs px-2 py-0.5 rounded-full"
                  style={{
                    backgroundColor: column.color + "20",
                    color: column.color,
                  }}
                >
                  {totalItemsInColumn}
                </span>
              </div>

              <div className={`flex-1 min-h-0 overflow-y-auto ${BOARD_CONTENT_PAD[viewSize]}`}>
                {/* First show folders in this column */}
                {foldersWithLevels.map(({ folder, level }) => {
                  const trackersInFolder = trackers.filter((t) => {
                    const groupId = getGroupRefId(t.group);
                    return groupId === folder.id;
                  });
                  // Only show trackers that belong to this column
                  const trackersInFolderForColumn = trackersInFolder.filter(
                    (tracker) => {
                      return getTrackerColumnId(tracker) === column.id;
                    },
                  );
                  const isExpanded = expandedFolders.has(folder.id);

                  return (
                    <ResourceGroupCard
                      key={`folder-${folder.id}-column-${column.id}`}
                      group={folder}
                      resourceCount={trackersInFolderForColumn.length}
                      isExpanded={isExpanded}
                      onToggle={() => onToggleFolder(folder.id)}
                      viewMode="board"
                      viewSize={viewSize}
                      level={level}
                    >
                      {trackersInFolderForColumn.map((tracker) =>
                        renderTracker(tracker),
                      )}
                    </ResourceGroupCard>
                  );
                })}

                {/* Then show ungrouped trackers */}
                {ungroupedTrackersInColumn.map((tracker) => (
                  <div key={tracker.id}>{renderTracker(tracker)}</div>
                ))}

                {/* Empty state */}
                {totalItemsInColumn === 0 && (
                  <div className="text-center py-8 text-content-tertiary">
                    <span className="text-sm">
                      Aucun tracker dans ce groupe
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TrackerBoardView;
